import React from 'react';
import type { FileRef, MedicaidEnrollmentDto, PracticeRequirementsDto } from '../types';
import type { TMedicaidVariant } from './seed';
import { stateName } from './MedicaidGroupFields';
import c from '../common.module.less';
import s from './s.module.less';

const yesNo = (b?: boolean | null) => {
    if (b === true) return 'Yes';
    if (b === false) return 'No';
    return '';
};
const fileName = (f?: FileRef | null) => f?.name || (f?.url ? f.url.split('/').pop() : '') || '';
const join = (...parts: (string | null | undefined)[]) => parts.filter(Boolean).join(' · ');
const maskTin = (o: { ssnOrTin?: string; ssnOrTinLast4?: string }) => {
    if (o.ssnOrTinLast4) return `•••-••-${o.ssnOrTinLast4}`;
    const d = (o.ssnOrTin || '').replace(/\D/g, '');
    return d.length >= 4 ? `•••-••-${d.slice(-4)}` : '';
};

const PLACE_LABEL: Record<string, string> = { LEASED: 'Leased or rented', OWNED: 'Owned', NEITHER: 'Neither' };
const PAY_LABEL: Record<string, string> = {
    DIRECT_DEPOSIT: 'Direct deposit to your payout account',
    PAPER_CHECK: 'Paper check',
};

const Item = ({ label, value }: { label: string; value?: React.ReactNode }) => (
    <div className={c.viewItem}>
        <div className={c.viewLabel}>{label}</div>
        <div className={c.viewValue}>{value || <span className={c.viewEmpty}>Not provided</span>}</div>
    </div>
);

// 原型 MCD_ROWS / MCD_DISC_ROWS + mcdVal 的只读行（Settings 和 Review 共用）
export const MedicaidEnrollmentView = ({
    value,
    variant,
    isCA,
    showOrgAgreement,
    enrollingIn,
}: {
    value?: MedicaidEnrollmentDto | null;
    variant: TMedicaidVariant;
    isCA?: boolean;
    showOrgAgreement?: boolean;
    // provider 级：在哪些州注册（Medicaid 答了 No 的州）
    enrollingIn?: string[];
}) => {
    const v = value || {};
    const rows: { label: string; value: React.ReactNode }[] = [];
    if (enrollingIn?.length) {
        rows.push({
            label: 'Enrolling in',
            value:
                enrollingIn.map(stateName).join(', ') +
                (variant === 'rendering' ? ' · as a rendering provider under your practice' : ''),
        });
    }
    if (variant === 'rendering') {
        rows.push({ label: 'Past Medicaid participation', value: yesNo(v.everMedicaid) });
    } else {
        rows.push(
            {
                label: 'Business license',
                value: v.licenseNotApplicable ? 'None' : join(v.licenseNumber, fileName(v.licenseFile)),
            },
            {
                label: 'Workers compensation insurance',
                value: v.workersComp ? join('Yes', fileName(v.workersCompFile)) : yesNo(v.workersComp),
            },
        );
        if (isCA)
            rows.push({
                label: 'Seller permit',
                value: v.sellerPermit ? join('Yes', v.sellerPermitNumber) : yesNo(v.sellerPermit),
            });
        rows.push(
            { label: 'Place of business', value: v.placeOfBusiness ? PLACE_LABEL[v.placeOfBusiness] : '' },
            { label: 'General liability insurance', value: join(v.glPolicyNumber, fileName(v.glFile)) },
        );
        if (showOrgAgreement)
            rows.push({
                label: 'Operating agreement',
                value: join(
                    fileName(v.orgAgreementFile),
                    v.orgAgreementSignedDate ? `signed ${v.orgAgreementSignedDate}` : '',
                ),
            });
        rows.push({ label: 'Laboratory services', value: yesNo(v.labServices) });
        if (isCA) rows.push({ label: 'Family PACT', value: yesNo(v.familyPact) });
        rows.push(
            {
                label: 'Owns or controls another health care provider',
                value: v.ownsOtherProviders ? join('Yes', v.otherProvidersList) : yesNo(v.ownsOtherProviders),
            },
            { label: 'Past Medicaid participation', value: yesNo(v.everMedicaid) },
            { label: 'Subcontractors', value: yesNo(v.subcontractors) },
            { label: 'Subcontractor transactions, last 5 years', value: yesNo(v.txnLast5y) },
            { label: 'Subcontractor transactions over $25,000, last 12 months', value: yesNo(v.txnOver25k) },
            { label: 'Government fines or debts', value: yesNo(v.govDebt) },
            { label: 'Medicaid payment method', value: v.payMethod ? PAY_LABEL[v.payMethod] : '' },
        );
    }
    if (variant !== 'group') {
        rows.push(
            { label: 'Civil fraud liability, last 10 years', value: yesNo(v.civilFraud) },
            { label: 'Settlement instead of conviction, last 10 years', value: yesNo(v.settlement) },
            { label: 'License surrendered during a hearing', value: yesNo(v.licenseSurrendered) },
            { label: 'Disciplined by a licensing authority', value: yesNo(v.licenseDisciplined) },
        );
    } else {
        const owners = (v.owners || []).filter((o) => o?.name);
        const dels = (v.delegated || []).filter((o) => o?.name);
        rows.push(
            {
                label: 'Owners and managing employees',
                value: owners.length ? (
                    <>
                        {owners.map((o, i) => (
                            // eslint-disable-next-line react/no-array-index-key
                            <div key={i}>
                                {join(
                                    [o.name, o.title].filter(Boolean).join(', '),
                                    o.ownershipPct != null ? `${o.ownershipPct}%` : '',
                                    maskTin(o),
                                )}
                            </div>
                        ))}
                    </>
                ) : (
                    ''
                ),
            },
            {
                label: 'Delegated officials',
                value:
                    v.hasDelegated === false
                        ? 'None'
                        : dels.map((d) => [d.name, d.title].filter(Boolean).join(', ')).join('; '),
            },
            { label: 'Authorized signer', value: [v.signerName, v.signerTitle].filter(Boolean).join(', ') },
        );
    }
    return (
        <div className={c.viewGrid}>
            {rows.map((r) => (
                <Item key={r.label} label={r.label} value={r.value} />
            ))}
        </div>
    );
};

// 原型 mcdGroupReviewHTML：每州一行；Review 上没答 / Yes 没 ID 的标红
export const MedicaidGroupStatesView = ({
    states,
    value,
}: {
    states: string[];
    value?: PracticeRequirementsDto | null;
}) => (
    <div className={c.viewGrid}>
        {states.map((st) => {
            const e = (value?.medicaid || []).find((m) => m.state === st);
            let val: React.ReactNode = <span className={s.viewMissing}>Not answered</span>;
            if (e?.enrolled === true)
                val = (
                    <>
                        Enrolled as a group ·{' '}
                        {e.certificationNumber || <span className={s.viewMissing}>ID not provided</span>}
                    </>
                );
            else if (e?.enrolled === false) val = 'Klarity is enrolling the practice';
            return <Item key={st} label={stateName(st)} value={val} />;
        })}
    </div>
);
